import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import type Owner from '@ember/owner';
import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import { formatCurrency } from 'tio-ui/format';
import Chip from 'tio-ui/components/chip';
import Button from 'tio-ui/components/button';
import { t } from 'ember-intl';
import {
  PhUsers,
  PhChartBar,
  PhTrendUp,
  PhTarget,
  PhArrowUp,
  PhWarning,
  PhCheckCircle,
  PhClock,
  PhGraduationCap,
  PhBriefcase,
  PhUsersFour,
  PhChartLine,
  PhSparkle,
  PhLightningA,
  PhQueue,
  PhCurrencyDollar,
} from 'ember-phosphor-icons';
import StatCard from 'tio-ui/components/stat-card';
import Card from 'tio-ui/components/card';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type { LiveTasData } from 'tio-employee/services/reimagined-data';
import {
  getCurrentDemoClientData,
  type DemoClientData,
} from 'tio-employee/utils/career-pathways-demo-data';

export default class CareerPathwaysAdminDashboard extends Component {
  @service declare reimaginedData: ReimaginedDataService;

  @tracked tasData?: LiveTasData;
  @tracked tasUsedAmount?: { yearToDate: number; isLiveData: boolean };

  constructor(owner: Owner, args: object) {
    super(owner, args);
    void this.loadTasMetrics();
  }

  loadTasMetrics = async () => {
    const [tasData, tasUsedAmount] = await Promise.all([
      this.reimaginedData.fetchTasData(),
      this.reimaginedData.fetchTasUsedAmount(),
    ]);
    this.tasData = tasData;
    this.tasUsedAmount = tasUsedAmount;
  };

  get tasSource(): DataSource {
    if (this.reimaginedData.isDemoMode) return 'demo';
    if (!this.tasData) return 'placeholder';
    return this.tasData.isLiveData ? 'live' : 'placeholder';
  }

  get demoData(): DemoClientData {
    return getCurrentDemoClientData();
  }

  get client() {
    return this.demoData.client;
  }

  get criticalRoles() {
    return this.demoData.criticalRoles;
  }

  get topPrograms() {
    return this.demoData.topPrograms;
  }

  get pipeline() {
    return this.demoData.pipeline;
  }

  // AI Insights based on client context
  get aiInsights() {
    const clientId = this.client.id;
    if (clientId === 'ups') {
      return [
        {
          type: 'risk',
          title: '5 drivers at risk of turnover',
          description: 'They have completed training but lack clear advancement path',
          action: 'Review career plans',
        },
        {
          type: 'opportunity',
          title: 'CDL training ROI exceeds target by 23%',
          description: 'Consider expanding program capacity',
          action: 'View analysis',
        },
        {
          type: 'action',
          title: '12 supervisors ready for promotion',
          description: 'Pipeline is strong, budget permits 8 promotions',
          action: 'Start reviews',
        },
      ];
    } else if (clientId === 'starbucks') {
      return [
        {
          type: 'risk',
          title: '8 baristas paused ASU enrollment',
          description: 'Scheduling conflicts detected in recent survey',
          action: 'Review schedules',
        },
        {
          type: 'opportunity',
          title: 'District manager pipeline healthy',
          description: '15 store managers are program-ready',
          action: 'View pipeline',
        },
        {
          type: 'action',
          title: '23 employees graduating this quarter',
          description: 'Recognize achievements and discuss career goals',
          action: 'Plan events',
        },
      ];
    }
    // Advocate (healthcare) default
    return [
      {
        type: 'risk',
        title: '3 RNs at flight risk',
        description: 'High performers without active development plans',
        action: 'Engage employees',
      },
      {
        type: 'opportunity',
        title: 'BSN program showing 340% ROI',
        description: 'Nurse retention 2.3x higher for participants',
        action: 'View report',
      },
      {
        type: 'action',
        title: '7 nurse managers ready for director track',
        description: 'Leadership assessment scores above threshold',
        action: 'Review candidates',
      },
    ];
  }

  // Pending approvals
  get pendingApprovals() {
    const clientId = this.client.id;
    if (clientId === 'ups') {
      return [
        { name: 'Marcus J.', program: 'CDL-A Training', amount: 4500, submitted: '2h ago' },
        { name: 'Jennifer S.', program: 'Supply Chain Cert', amount: 2800, submitted: '5h ago' },
        { name: 'Robert K.', program: 'Supervisor Development', amount: 1500, submitted: '1d ago' },
      ];
    } else if (clientId === 'starbucks') {
      return [
        { name: 'Ashley M.', program: 'ASU Online - Business', amount: 3200, submitted: '1h ago' },
        { name: 'Tyler R.', program: 'Barista Champion Cert', amount: 450, submitted: '3h ago' },
        { name: 'Maria G.', program: 'Store Manager Program', amount: 1800, submitted: '6h ago' },
      ];
    }
    return [
      { name: 'Sarah M.', program: 'RN-to-BSN', amount: 5500, submitted: '1h ago' },
      { name: 'James K.', program: 'Leadership Essentials', amount: 2200, submitted: '4h ago' },
      { name: 'Maria R.', program: 'Clinical Specialty', amount: 3800, submitted: '1d ago' },
    ];
  }

  // Budget utilization
  get budgetData() {
    const clientId = this.client.id;
    if (clientId === 'ups') {
      return { used: 2.1, total: 3.5, percentage: 60 };
    } else if (clientId === 'starbucks') {
      return { used: 18.5, total: 25, percentage: 74 };
    }
    return { used: 4.2, total: 5.5, percentage: 76 };
  }

  // Generate recent activity based on client context
  get recentActivity() {
    const clientId = this.client.id;
    if (clientId === 'ups') {
      return [
        {
          type: 'completion',
          employee: 'Mike T.',
          action: 'completed CDL-A Training',
          time: '2 hours ago',
        },
        {
          type: 'enrollment',
          employee: 'Lisa M.',
          action: 'enrolled in Supervisor Leadership',
          time: '4 hours ago',
        },
        {
          type: 'promotion',
          employee: 'Carlos R.',
          action: 'promoted to Hub Supervisor',
          time: '1 day ago',
        },
        {
          type: 'milestone',
          employee: 'James K.',
          action: 'reached 75% of career plan',
          time: '2 days ago',
        },
      ];
    } else if (clientId === 'starbucks') {
      return [
        {
          type: 'completion',
          employee: 'Emma S.',
          action: 'completed ASU semester',
          time: '1 hour ago',
        },
        {
          type: 'enrollment',
          employee: 'Jordan M.',
          action: 'enrolled in Store Manager Development',
          time: '3 hours ago',
        },
        {
          type: 'promotion',
          employee: 'Taylor R.',
          action: 'promoted to Shift Supervisor',
          time: '1 day ago',
        },
        {
          type: 'milestone',
          employee: 'Alex P.',
          action: 'earned 30 credits toward degree',
          time: '2 days ago',
        },
      ];
    }
    // Advocate (healthcare) default
    return [
      {
        type: 'completion',
        employee: 'Sarah M.',
        action: 'completed Leadership Essentials',
        time: '2 hours ago',
      },
      {
        type: 'enrollment',
        employee: 'James K.',
        action: 'enrolled in RN-to-BSN program',
        time: '4 hours ago',
      },
      {
        type: 'promotion',
        employee: 'Maria R.',
        action: 'promoted to Nurse Manager',
        time: '1 day ago',
      },
      {
        type: 'milestone',
        employee: 'David L.',
        action: 'reached 50% of career plan',
        time: '2 days ago',
      },
    ];
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="data-audit w-full min-w-0 pb-12">
      {{! Header }}
      <div class="tio-row-between mb-6">
        <div>
          <Heading @level={{1}} class="app-page-title">
            Career Pathways Dashboard
          </Heading>
          <Text class="tio-fg-secondary">{{this.client.name}}
            —
            {{this.client.employeeCount}}
            employees</Text>
        </div>
        <div class="tio-flex">
          <Button @appearance="outlined" @intent="primary">
            <PhChartBar @size={{18}} class="mr-2" />
            Export Report
          </Button>
          <Button @intent="primary">
            <PhTarget @size={{18}} class="mr-2" />
            Configure Goals
          </Button>
        </div>
      </div>

      {{! AI Insights Banner }}
      <div class="tio-ai-insights-banner mb-6 p-4 rounded-xl">
        <div class="tio-row mb-4">
          <div class="tio-ai-insights-icon p-2 rounded-lg shadow-lg">
            <PhSparkle @size={{20}} weight="fill" />
          </div>
          <div>
            <Text @weight="semibold">AI Insights</Text>
            <Text @size="sm" class="tio-fg-secondary">3 recommendations for your attention</Text>
          </div>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          {{#each this.aiInsights as |insight|}}
            <div class="tio-translucent-surface p-3 rounded-lg border tio-border-subtle">
              <div class="tio-row-top-tight mb-2">
                {{#if (eq insight.type "risk")}}
                  <PhWarning @size={{16}} class="tio-fg-danger mt-0.5" />
                {{else if (eq insight.type "opportunity")}}
                  <PhTrendUp @size={{16}} class="tio-fg-success mt-0.5" />
                {{else}}
                  <PhLightningA @size={{16}} class="tio-fg-warning mt-0.5" />
                {{/if}}
                <Text @size="sm" @weight="medium">{{insight.title}}</Text>
              </div>
              <Text @size="sm" class="tio-fg-secondary mb-2">{{insight.description}}</Text>
              <button type="button" class="tio-link-brand-inline tio-text-sm-medium">
                {{insight.action}}
                →
              </button>
            </div>
          {{/each}}
        </div>
      </div>

      {{! KPI Cards }}
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          @icon={{PhUsers}}
          @label="TA Utilization"
          @value="{{this.client.taUtilization}}%"
          @color="violet"
        >
          <:extra>
            <div class="tio-row-tight">
              <span class="tio-row-flush tio-fg-success text-sm">
                <PhArrowUp @size={{14}} />
                3.2%
              </span>
              <Text @size="sm" class="tio-fg-secondary">
                {{#if (eq this.client.id "starbucks")}}
                  Highest in retail sector
                {{else if (eq this.client.id "ups")}}
                  Opportunity for growth
                {{else}}
                  Strong for healthcare
                {{/if}}
              </Text>
            </div>
          </:extra>
        </StatCard>

        <StatCard
          @icon={{PhTarget}}
          @label="Plan Activation Rate"
          @value="{{this.client.planActivation}}%"
          @color="teal"
        >
          <:extra>
            <div class="tio-row-tight">
              <span class="tio-row-flush tio-fg-success text-sm">
                <PhArrowUp @size={{14}} />
                8.5%
              </span>
              <Text @size="sm" class="tio-fg-secondary">Employees with active career plans</Text>
            </div>
          </:extra>
        </StatCard>

        <StatCard
          @icon={{PhGraduationCap}}
          @label="Program Completion"
          @value="{{this.client.completionRate}}%"
          @color="green"
        >
          <:extra>
            <div class="tio-row-tight">
              <span class="tio-row-flush tio-fg-success text-sm">
                <PhArrowUp @size={{14}} />
                5.1%
              </span>
              <Text @size="sm" class="tio-fg-secondary">Of enrolled complete programs</Text>
            </div>
          </:extra>
        </StatCard>

        <StatCard
          @icon={{PhTrendUp}}
          @label="Internal Mobility"
          @value="{{this.client.internalMobility}}%"
          @color="amber"
        >
          <:extra>
            <div class="tio-row-tight">
              <span class="tio-row-flush tio-fg-success text-sm">
                <PhArrowUp @size={{14}} />
                2.8%
              </span>
              <Text @size="sm" class="tio-fg-secondary">Promotions from TA participants (12mo)</Text>
            </div>
          </:extra>
        </StatCard>
      </div>

      {{! TAS Metrics }}
      {{#if this.tasData}}
        <DataIndicator
          @source={{this.tasSource}}
          @showBadge={{true}}
          @dataSourceId="admin-tas-metrics"
        >
          <Card class="mb-6">
            <div class="tio-row-between mb-4">
              <div>
                <Heading @level={{2}} @size="lg">
                  Tuition Assistance Metrics
                </Heading>
                <Text @size="sm" class="tio-fg-secondary">
                  {{t "tuition_assistance.reimagined.dashboard.section_momentum"}}
                </Text>
              </div>
              <Chip @intent={{if (eq this.tasSource "live") "success" "warning"}} @size="sm">
                {{if (eq this.tasSource "live") "Live" "Demo"}}
              </Chip>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div class="rounded-xl border tio-border-default p-4">
                <div class="tio-row-tight text-sm tio-fg-secondary">
                  <PhTarget @size={{16}} class="tio-fg-brand" />
                  {{t "tuition_assistance.reimagined.dashboard.metric_approved"}}
                </div>
                <Heading @level={{3}}>
                  {{formatCurrency this.tasData.summary.totalApproved}}
                </Heading>
              </div>
              <div class="rounded-xl border tio-border-default p-4">
                <div class="tio-row-tight text-sm tio-fg-secondary">
                  <PhCurrencyDollar @size={{16}} class="tio-fg-success" />
                  {{t "tuition_assistance.reimagined.dashboard.metric_paid"}}
                </div>
                <Heading @level={{3}}>
                  {{formatCurrency this.tasData.summary.totalPaid}}
                </Heading>
              </div>
              <div class="rounded-xl border tio-border-default p-4">
                <div class="tio-row-tight text-sm tio-fg-secondary">
                  <PhCheckCircle @size={{16}} class="tio-fg-info" />
                  {{t "tuition_assistance.reimagined.dashboard.metric_active"}}
                </div>
                <Heading @level={{3}}>
                  {{this.tasData.summary.activeCount}}
                </Heading>
              </div>
              <div class="rounded-xl border tio-border-default p-4">
                <div class="tio-row-tight text-sm tio-fg-secondary">
                  <PhClock @size={{16}} class="tio-fg-warning" />
                  {{t "tuition_assistance.reimagined.dashboard.metric_pending"}}
                </div>
                <Heading @level={{3}}>
                  {{this.tasData.summary.pendingCount}}
                </Heading>
              </div>
            </div>
            {{#if this.tasUsedAmount}}
              <div class="mt-4 text-sm tio-fg-secondary">
                {{t "tuition_assistance.reimagined.dashboard.card_used_this_year"}}
                ·
                {{formatCurrency this.tasUsedAmount.yearToDate}}
              </div>
            {{/if}}
          </Card>
        </DataIndicator>
      {{/if}}

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {{! Main Column }}
        <div class="lg:col-span-2 space-y-6">
          {{! Budget + Approvals Row }}
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            {{! Budget Utilization }}
            <Card>
              <div class="tio-row-between mb-4">
                <div class="tio-row-tight">
                  <PhCurrencyDollar @size={{20}} class="tio-fg-success" />
                  <Text @weight="semibold">Budget Utilization</Text>
                </div>
                <Text @size="sm" class="tio-fg-secondary">FY 2024</Text>
              </div>
              <div class="tio-row-center mb-4">
                {{! SVG Progress Ring }}
                <div class="relative">
                  <svg width="120" height="120" viewBox="0 0 120 120" class="transform -rotate-90">
                    <circle
                      cx="60"
                      cy="60"
                      r="50"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="12"
                      class="tio-fg-faint"
                    />
                    <circle
                      cx="60"
                      cy="60"
                      r="50"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="12"
                      stroke-linecap="round"
                      stroke-dasharray="314"
                      stroke-dashoffset="{{if
                        (eq this.client.id 'ups')
                        '126'
                        (if (eq this.client.id 'starbucks') '82' '75')
                      }}"
                      class="tio-fg-success"
                    />
                  </svg>
                  <div class="absolute inset-0 tio-place-center text-center">
                    <span
                      class="tio-text-2xl-bold tio-fg-primary"
                    >{{this.budgetData.percentage}}%</span>
                    <span class="text-xs tio-fg-muted">utilized</span>
                  </div>
                </div>
              </div>
              <div class="text-center">
                <Text @weight="semibold">${{this.budgetData.used}}M</Text>
                <Text class="tio-fg-muted">
                  of ${{this.budgetData.total}}M budget</Text>
              </div>
            </Card>

            {{! Pending Approvals }}
            <Card>
              <div class="tio-row-between mb-4">
                <div class="tio-row-tight">
                  <PhQueue @size={{20}} class="tio-fg-brand" />
                  <Text @weight="semibold">Pending Approvals</Text>
                </div>
                <Chip @intent="warning" @size="sm">{{this.pendingApprovals.length}}</Chip>
              </div>
              <div class="space-y-3">
                {{#each this.pendingApprovals as |approval|}}
                  <div class="tio-row-between p-2 tio-surface-sunken">
                    <div class="min-w-0">
                      <Text @size="sm" @weight="medium" class="truncate">{{approval.name}}</Text>
                      <Text @size="xs" class="tio-fg-muted truncate">{{approval.program}}</Text>
                    </div>
                    <div class="text-right shrink-0">
                      <Text @size="sm" @weight="semibold">${{approval.amount}}</Text>
                      <Text @size="xs" class="tio-fg-muted">{{approval.submitted}}</Text>
                    </div>
                  </div>
                {{/each}}
              </div>
              <Button
                @intent="brand"
                @appearance="minimal"
                @size="sm"
                @isFullWidth={{true}}
                class="mt-3"
              >
                View All Approvals →
              </Button>
            </Card>
          </div>

          {{! Critical Role Shortages }}
          <Card @padding="none">
            <div class="tio-row-between p-5 border-b tio-border-subtle">
              <div class="tio-row-tight">
                <PhWarning @size={{20}} class="tio-fg-danger" />
                <Text @weight="semibold">Critical Role Shortages</Text>
              </div>
              <LinkTo @route="authenticated.reimagined.admin.skills-inventory">
                <Button @appearance="outlined" @size="sm">View All Gaps</Button>
              </LinkTo>
            </div>
            <div class="p-5 space-y-4">
              {{#each this.criticalRoles as |role|}}
                <div class="tio-row-loose p-4 tio-surface-sunken">
                  <div class="flex-1">
                    <div class="tio-row-tight">
                      <Text @weight="semibold">{{role.title}}</Text>
                      {{#if (eq role.risk "critical")}}
                        <Chip @intent="danger" @size="sm">Critical</Chip>
                      {{else if (eq role.risk "high")}}
                        <Chip @intent="warning" @size="sm">High</Chip>
                      {{else}}
                        <Chip @intent="default" @size="sm">Medium</Chip>
                      {{/if}}
                    </div>
                    <div class="tio-row-loose mt-2 text-sm tio-fg-muted">
                      <span class="tio-fg-danger font-medium">Gap:
                        {{role.gap}}</span>
                      <span>Retirements: {{role.retirements}}</span>
                    </div>
                  </div>
                  <div class="text-right">
                    <div class="tio-row-extra-tight tio-fg-success">
                      <PhUsers @size={{16}} />
                      <span class="font-semibold">{{role.pipeline}}</span>
                    </div>
                    <Text @size="sm" class="tio-fg-muted">in pipeline</Text>
                  </div>
                </div>
              {{/each}}
            </div>
          </Card>

          {{! Top Performing Programs }}
          <Card @padding="none">
            <div class="tio-row-between p-5 border-b tio-border-subtle">
              <div class="tio-row-tight">
                <PhChartLine @size={{20}} class="tio-fg-brand" />
                <Text @weight="semibold">Top Performing Programs</Text>
              </div>
              <LinkTo @route="authenticated.reimagined.admin.program-effectiveness">
                <Button @appearance="outlined" @size="sm">View All Programs</Button>
              </LinkTo>
            </div>
            <div class="p-5 overflow-x-auto">
              <table class="w-full">
                <thead>
                  <tr class="text-left text-sm tio-fg-muted border-b">
                    <th class="pb-3 font-medium">Program</th>
                    <th class="pb-3 font-medium">Provider</th>
                    <th class="pb-3 font-medium text-center">Enrolled</th>
                    <th class="pb-3 font-medium text-center">Completion</th>
                    <th class="pb-3 font-medium text-right">ROI</th>
                  </tr>
                </thead>
                <tbody class="divide-y tio-divide-subtle">
                  {{#each this.topPrograms as |program|}}
                    <tr>
                      <td class="py-3">
                        <Text @weight="medium">{{program.name}}</Text>
                      </td>
                      <td class="py-3 tio-fg-secondary text-sm">{{program.provider}}</td>
                      <td class="py-3 text-center tio-fg-secondary">{{program.enrollments}}</td>
                      <td class="py-3 text-center">
                        <Chip @intent="success" @size="sm">{{program.completionRate}}%</Chip>
                      </td>
                      <td class="py-3 text-right font-semibold tio-fg-success">{{program.roi}}%</td>
                    </tr>
                  {{/each}}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {{! Sidebar }}
        <div class="space-y-6">
          {{! Leadership Pipeline }}
          <Card @padding="none">
            <div class="tio-row-between p-5 border-b tio-border-subtle">
              <div class="tio-row-tight">
                <PhUsersFour @size={{20}} class="tio-fg-brand" />
                <Text @weight="semibold">Leadership Pipeline</Text>
              </div>
              <LinkTo @route="authenticated.reimagined.admin.leadership">
                <Button @appearance="outlined" @size="sm">Manage</Button>
              </LinkTo>
            </div>
            <div class="p-5 space-y-3">
              <div class="tio-leadership-pipeline-tier--identified tio-row-between p-3 rounded-lg">
                <div>
                  <Text @weight="semibold">Identified</Text>
                  <Text @size="sm" class="tio-fg-muted">High potential</Text>
                </div>
                <span class="tio-fg-brand tio-text-2xl-bold">{{this.pipeline.identified}}</span>
              </div>
              <div class="tio-leadership-pipeline-tier--active tio-row-between p-3 rounded-lg">
                <div>
                  <Text @weight="semibold">In Program</Text>
                  <Text @size="sm" class="tio-fg-muted">Actively developing</Text>
                </div>
                <span class="tio-fg-brand tio-text-2xl-bold">{{this.pipeline.inProgram}}</span>
              </div>
              <div class="tio-row-between p-3 tio-bg-info-soft rounded-lg">
                <div>
                  <Text @weight="semibold">Developing</Text>
                  <Text @size="sm" class="tio-fg-muted">In training</Text>
                </div>
                <span class="tio-text-2xl-bold tio-fg-info">{{this.pipeline.developing}}</span>
              </div>
              <div class="tio-row-between p-3 tio-bg-success-soft rounded-lg">
                <div>
                  <Text @weight="semibold">Ready Now</Text>
                  <Text @size="sm" class="tio-fg-muted">Promotion ready</Text>
                </div>
                <span class="tio-text-2xl-bold tio-fg-success">{{this.pipeline.readyNow}}</span>
              </div>
              <div class="pt-3 mt-3 border-t tio-border-default">
                <div class="tio-row-between text-sm">
                  <Text class="tio-fg-muted">Promoted YTD</Text>
                  <Text @weight="semibold" class="tio-fg-success">{{this.pipeline.promoted}}</Text>
                </div>
                <div class="tio-row-between text-sm mt-2">
                  <Text class="tio-fg-muted">Attrition</Text>
                  <Text @weight="semibold" class="tio-fg-danger">{{this.pipeline.attrition}}</Text>
                </div>
              </div>
            </div>
          </Card>

          {{! Recent Activity }}
          <Card @padding="none">
            <div class="tio-row-tight p-5 border-b tio-border-subtle">
              <PhClock @size={{20}} class="tio-fg-muted" />
              <Text @weight="semibold">Recent Activity</Text>
            </div>
            <div class="p-5 space-y-4">
              {{#each this.recentActivity as |activity|}}
                <div class="tio-row-top">
                  <div class="mt-1">
                    {{#if (eq activity.type "completion")}}
                      <PhCheckCircle @size={{18}} class="tio-fg-success" />
                    {{else if (eq activity.type "enrollment")}}
                      <PhGraduationCap @size={{18}} class="tio-fg-brand-accent" />
                    {{else if (eq activity.type "promotion")}}
                      <PhTrendUp @size={{18}} class="tio-fg-warning" />
                    {{else}}
                      <PhTarget @size={{18}} class="tio-fg-info" />
                    {{/if}}
                  </div>
                  <div class="tio-fill-min">
                    <Text>
                      <span class="font-medium">{{activity.employee}}</span>
                      {{activity.action}}
                    </Text>
                    <Text @size="sm" class="tio-fg-muted">{{activity.time}}</Text>
                  </div>
                </div>
              {{/each}}
            </div>
          </Card>

          {{! Quick Links }}
          <Card @padding="none">
            <div class="tio-row-tight p-5 border-b tio-border-subtle">
              <PhBriefcase @size={{20}} class="tio-fg-muted" />
              <Text @weight="semibold">Quick Actions</Text>
            </div>
            <div class="p-5 space-y-2">
              <LinkTo
                @route="authenticated.reimagined.admin.team"
                class="block p-3 hover:tio-bg-sunken rounded-lg transition-colors"
              >
                <Text @weight="medium" class="tio-fg-brand">View Team Progress</Text>
              </LinkTo>
              <LinkTo
                @route="authenticated.reimagined.admin.skills-inventory"
                class="block p-3 hover:tio-bg-sunken rounded-lg transition-colors"
              >
                <Text @weight="medium" class="tio-fg-brand">Skills Inventory</Text>
              </LinkTo>
              <LinkTo
                @route="authenticated.reimagined.admin.leadership"
                class="block p-3 hover:tio-bg-sunken rounded-lg transition-colors"
              >
                <Text @weight="medium" class="tio-fg-brand">Leadership Pipeline</Text>
              </LinkTo>
              <LinkTo
                @route="authenticated.reimagined.admin.program-effectiveness"
                class="block p-3 hover:tio-bg-sunken rounded-lg transition-colors"
              >
                <Text @weight="medium" class="tio-fg-brand">Program ROI</Text>
              </LinkTo>
            </div>
          </Card>
        </div>
      </div>
    </VStack>
  </template>
}
